import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Col, Row } from 'reactstrap';
import FormActions from 'views/components/FormActions';
import { v4 as uuidv4 } from 'uuid';
import DropdownInput from 'views/formComponents/DropdownInput';
import Membership from 'views/formComponents/Membership';
import SwitchInput from 'views/formComponents/SwitchInput';
import TextInput from 'views/formComponents/TextInput';
import { DB_FIELD_LENGTHS, UI_ONLY_MAX_LENGTHS } from 'utility/constants/dbFieldLengths';
import FetchEntities from 'views/screens/Entities/views/FetchEntities';
import * as organizationActions from '../../../actions';

interface Props {
  isSlidingPaneOpen: boolean;
  closeSlidingPane: () => void;
}

const SlidingPaneQuickCreateOrganization = ({ isSlidingPaneOpen, closeSlidingPane }: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      organizationName: '',
      tradeName: '',
      membership: null,               // { operatorId, agentId }
      useDivisionName: true,
      useSubdivisionName: true,
      divisionName: '',
      subdivisionName: '',
      organizationType: 1,
      alertConsoleActive: false,
      comments: ''
    }
  });

  const formValues = watch();

  const organizationTypes = [...Array(4).keys()].map((id) => ({
    id: id + 1,
    value: id + 1,
    label: <FormattedMessage id={`organizations.organizationType${id + 1}`} />,
    label_intl: intl.formatMessage({ id: `organizations.organizationType${id + 1}` })
  }));

  const handleSave = (values: any) => {
    const name = (values.organizationName || '').trim();
    const trade = (values.tradeName || '').trim();
    const division = values.useDivisionName ? name : (values.divisionName || '').trim();
    const subdivision = values.useSubdivisionName ? name : (values.subdivisionName || '').trim();

    dispatch(
      organizationActions.startQuickCreateOrganization({
        organizationName: name,
        tradeName: trade,
        organizationType: values.organizationType || 1,
        operatorId: values.membership?.operatorId,
        agentId: values.membership?.agentId,
        alertConsoleActive: values.alertConsoleActive,
        contactEmail: '', // Default empty value as required by SP
        contactPhone: null,
        contactName: null,
        organizationAddress: null,
        comments: values.comments?.trim() || '',
        contacts: '', // Required by SP signature
        divisionName: division,
        subdivisionName: subdivision
      })
    );

    reset();
    closeSlidingPane();
  };

  return (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName="custom-overlay-sliding-pane"
      isOpen={isSlidingPaneOpen}
      onRequestClose={closeSlidingPane}
      title={intl.formatMessage({ id: 'organizations.quickCreate' })}
      width={'40rem'}
    >
      <FetchEntities useOperators useAgents />

      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="organizationName"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id="organizationNameInput"
                required
                label={<FormattedMessage id="organizations.organizationName" />}
                type="text"
                maxLength={DB_FIELD_LENGTHS.Organizations.OrganizationName}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="tradeName"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id="tradeNameInput"
                required
                label={<FormattedMessage id="organizations.tradeName" />}
                type="text"
                maxLength={DB_FIELD_LENGTHS.Organizations.TradeName}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="organizationType"
            render={({ field, fieldState }) => (
              <DropdownInput
                field={field}
                fieldstate={fieldState}
                id="organizationTypeDropdown"
                options={organizationTypes}
                label={<FormattedMessage id="organizations.organizationType" />}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Membership
          control={control}
          name="membership"
          options={{
            useOrganization: false,
            useDivision: false,
            useSubdivision: false,
            useUserOperator: true,
            setUserOperator: true,
            useAgent: true,
            setUserAgent: true,
            colSize: 12
          }}
        />
      </Row>
      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="useDivisionName"
            render={({ field }) => (
              <SwitchInput
                field={field}
                label={
                  <>
                    <FormattedMessage id="organizations.useOrganizationName" /> (División)
                  </>
                }
              />
            )}
          />
        </Col>
      </Row>
      {!formValues.useDivisionName && (
        <Row>
          <Col sm="12">
            <Controller
              control={control}
              name="divisionName"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextInput
                  field={field}
                  fieldstate={fieldState}
                  id="divisionNameInput"
                  required
                  label={<FormattedMessage id="organizations.divisionName" />}
                  type="text"
                  maxLength={DB_FIELD_LENGTHS.Divisions.DivisionName}
                />
              )}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="useSubdivisionName"
            render={({ field }) => (
              <SwitchInput
                field={field}
                label={
                  <>
                    <FormattedMessage id="organizations.useOrganizationName" /> (Subdivisión)
                  </>
                }
              />
            )}
          />
        </Col>
      </Row>
      {!formValues.useSubdivisionName && (
        <Row>
          <Col sm="12">
            <Controller
              control={control}
              name="subdivisionName"
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <TextInput
                  field={field}
                  fieldstate={fieldState}
                  id="subdivisionNameInput"
                  required
                  label={<FormattedMessage id="organizations.subdivisionName" />}
                  type="text"
                  maxLength={DB_FIELD_LENGTHS.Subdivisions.SubdivisionName}
                />
              )}
            />
          </Col>
        </Row>
      )}
      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="alertConsoleActive"
            render={({ field }) => (
              <SwitchInput
                field={field}
                label={<FormattedMessage id="organizations.alertConsoleActive" />}
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col sm="12">
          <Controller
            control={control}
            name="comments"
            render={({ field, fieldState }) => (
              <TextInput
                field={field}
                fieldstate={fieldState}
                id="commentsInput"
                label={<FormattedMessage id="organizations.comments" />}
                type="textarea"
                maxLength={UI_ONLY_MAX_LENGTHS.GENERIC_COMMENTS}
              />
            )}
          />
        </Col>
      </Row>
      
      <FormActions
        variant="footer"
        saveType="button"
        saveDisabled={!isValid || isSubmitting}
        onSave={handleSubmit(handleSave)}
        onCancel={() => {
          reset();
          closeSlidingPane();
        }}
      />
    </SlidingPane>
  );
};

export default SlidingPaneQuickCreateOrganization;